import { diff } from 'deep-object-diff';
import { beginWrite, endWrite } from 'hooks/data-saving/saveStatusStore';
import useAuth from 'hooks/useAuth';
import useCreateGroupNotification from 'hooks/useCreateGroupNotification';
import useCreateSingleNotification from 'hooks/useCreateSingleNotification';
import useErrorSnackbar from 'hooks/useErrorSnackbar';
import useStatusChangeActions from 'hooks/useStatusChangeActions';
import isEqual from 'lodash/isEqual';
import { useState } from 'react';
import { dispatch } from 'store';
import { setDialogOpen } from 'store/reducers/dialog';
import { setLastSaved } from 'store/reducers/ui';
import { Event, eventInit } from 'types/event';
import normalizeEmail, { emailIndexId } from 'utils/normalizeEmail';
import sanitizeDiff from 'utils/sanitizeDiff';

const EVENT_WIPE_PROTECTED_KEYS: (keyof Event)[] = [
  'bookedDateActual',
  'createdBy',
  'createdDate',
  'dateStart',
  'eventTitle',
  'eventType',
  'clients',
  'clientIds',
  'venue',
  'staticDetailsFields',
  'flexDetailsFields'
];

const EVENT_WIPE_CRITICAL_KEYS: (keyof Event)[] = [
  'clients',
  'clientIds',
  'staticDetailsFields',
  'flexDetailsFields'
];

/**
 * Returns true when `value` looks like the eventInit default for the given key.
 * Uses strict deep-equality against eventInit so only exact default matches are flagged —
 * no generic falsy/empty checks that would produce false positives for legitimate user edits.
 */
const isDefaultLike = (value: unknown, key: keyof Event): boolean => {
  if (value === undefined) return true;
  return isEqual(value, eventInit[key]);
};

const getUnsafeEventDefaultOverwriteKeys = (changes: Record<string, any>, originalObject: any) =>
  EVENT_WIPE_PROTECTED_KEYS.filter((key) => {
    if (!Object.prototype.hasOwnProperty.call(changes, key)) return false;
    const originalValue = originalObject?.[key];
    const nextValue = changes[key];
    return !isDefaultLike(originalValue, key) && isDefaultLike(nextValue, key);
  });

const isLikelyEventWipe = (defaultedKeys: (keyof Event)[]) => {
  if (defaultedKeys.length >= 3) return true;
  return defaultedKeys.some((key) => EVENT_WIPE_CRITICAL_KEYS.includes(key));
};

const useSaveObject = () => {
  const firebase = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const errorSnackbar = useErrorSnackbar();
  const createGroupNotificaiton = useCreateGroupNotification();
  const createSingleNotification = useCreateSingleNotification();
  const { onMarkedDead, onDateChange } = useStatusChangeActions();

  const notify = (notificationName: string) => {
    createGroupNotificaiton(notificationName);
  };

  const notifySingle = (id: string, notificationName: string) => {
    createSingleNotification(id, notificationName);
  };

  const saveObject = async <T extends {}>(
    collection: string,
    id: string,
    object: T,
    originalObject: T
  ) => {
    // Flex fields are stored as a single array. Building a deep numeric-index diff only to throw it
    // away in sanitizeDiff is expensive for rich text/custom columns. Compare once (shared rows
    // short-circuit), then supply exactly the same whole-array replacement to the existing guards.
    let changes: Record<string, any>;
    const next = object as Record<string, any>;
    const previous = originalObject as Record<string, any>;
    if (
      collection === 'events' &&
      Array.isArray(next.flexDetailsFields) &&
      Array.isArray(previous.flexDetailsFields)
    ) {
      const { flexDetailsFields: nextFields, ...nextRest } = next;
      const { flexDetailsFields: previousFields, ...previousRest } = previous;
      changes = sanitizeDiff(diff(previousRest, nextRest), nextRest);
      if (!isEqual(previousFields, nextFields)) changes.flexDetailsFields = nextFields;
    } else {
      changes = sanitizeDiff(diff(originalObject, object), object);
    }
    let didSave = false;
    // Tracked separately from `didSave` so the in-flight counter is balanced even when the write throws.
    let didBeginWrite = false;
    const didChangeDateStart = Object.prototype.hasOwnProperty.call(changes, 'dateStart');
    const didChangeContactEmail =
      collection === 'contacts' && Object.prototype.hasOwnProperty.call(changes, 'email1');
    // console.log('changes', changes);
    try {
      if (Object.keys(changes).length === 0) {
        return true;
      }

      if (didChangeContactEmail) {
        // All new writes use a canonical join key. Keeping a separately indexed
        // value lets the transaction below serialize competing email changes.
        const email = normalizeEmail(changes.email1);
        changes.email1 = email;
        changes.email1Normalized = email;
      }
      if (collection === 'events') {
        const unsafeDefaultedKeys = getUnsafeEventDefaultOverwriteKeys(changes, originalObject);
        if (isLikelyEventWipe(unsafeDefaultedKeys)) {
          console.error('Blocked unsafe event save that looked like a data wipe', {
            id,
            unsafeDefaultedKeys,
            changes
          });
          errorSnackbar({ message: 'Blocked unsafe event save. Please tell Todd what you were doing.' });
          return;
        }

        if (didChangeDateStart && !changes.dateStart) {
          const linkedGigs = await firebase.db.collection('gigs').where('relEventId', '==', id).limit(1).get();

          if (!linkedGigs.empty) {
            errorSnackbar({
              message: 'Cannot clear the event date while gigs exist. Reassign or remove the gigs first.'
            });
            return;
          }
        }
      }
      setIsSaving(true);
      beginWrite();
      didBeginWrite = true;
      if (changes.status === 'booked') {
        changes.bookedDateActual = new Date();
        changes.taskProgress = { done: 0, total: 0 };
      }
      if (changes.status === 'dead') {
        changes.taskProgress = null;
      }
      const docRef = firebase.db.collection(collection).doc(id);
      const write = { ...changes, modified: new Date() };

      if (didChangeContactEmail) {
        await firebase.db.runTransaction(async (transaction) => {
          const indexes = firebase.db.collection('contactEmailIndex');
          const newIndexRef = changes.email1
            ? indexes.doc(emailIndexId(changes.email1))
            : null;
          const oldEmail = normalizeEmail((originalObject as any).email1);
          const oldIndexRef = oldEmail ? indexes.doc(emailIndexId(oldEmail)) : null;
          const [newIndex, oldIndex] = await Promise.all([
            newIndexRef ? transaction.get(newIndexRef) : Promise.resolve(null),
            oldIndexRef ? transaction.get(oldIndexRef) : Promise.resolve(null)
          ]);
          if (newIndex?.exists && newIndex.data()?.contactId !== id) {
            throw new Error('This email is already registered to another contact.');
          }
          transaction.set(docRef, write, { merge: true });
          if (newIndexRef) transaction.set(newIndexRef, { contactId: id });
          if (
            oldEmail &&
            oldEmail !== changes.email1 &&
            oldIndexRef &&
            oldIndex?.exists &&
            oldIndex.data()?.contactId === id
          ) {
            transaction.delete(oldIndexRef);
          }
        });
      } else {
        await docRef.set(write, { merge: true });
      }
      didSave = true;
      dispatch(setLastSaved(new Date()));
      return true;
    } catch (e: any) {
      errorSnackbar(e);
      return false;
    } finally {
      // notifications
      if (didSave && collection === 'events') {
        if (Object.keys(changes).includes('status')) {
          if (changes.status === 'booked') {
            dispatch(
              setDialogOpen({
                dialogOpen: true,
                dialogButtons: ['No', 'Yes'],
                dialogContent: 'Send notifications for this booking?',
                dialogFunction: () => notify('Event Booked')
              })
            );
          }
          if (changes.status === 'dead') {
            onMarkedDead(id, originalObject as unknown as Event);
          }
        }
        if (didChangeDateStart) {
          await onDateChange(id, changes.dateStart);
        }
        if (Object.keys(changes).includes('producer') && changes.producer.id) {
          dispatch(
            setDialogOpen({
              dialogOpen: true,
              dialogButtons: ['No', 'Yes'],
              dialogContent: 'Send a notification to the producer?',
              dialogFunction: () => notifySingle(changes.producer.id, 'Producer Set')
            })
          );
        }
      }
      setIsSaving(false);
      if (didBeginWrite) endWrite();
    }
  };

  return { isSaving, saveObject };
};

export default useSaveObject;
